import React, { useEffect, useState } from 'react';
import { Download, X, Share } from 'lucide-react';

const DISMISS_KEY = 'capex_pwa_dismissed_at';
const DISMISS_DAYS = 10;

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

const isMobile = () => /android|iphone|ipad|ipod/i.test(navigator.userAgent);
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;

const recentlyDismissed = () => {
  try {
    const t = localStorage.getItem(DISMISS_KEY);
    if (!t) return false;
    return Date.now() - Number(t) < DISMISS_DAYS * 86400000;
  } catch { return false; }
};

export default function PwaInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone() || !isMobile() || recentlyDismissed()) return undefined;

    // Événement déjà capté au démarrage (index.js) ?
    if (window.__capexBip) {
      setDeferred(window.__capexBip);
      setVisible(true);
    }

    const onBip = (e) => {
      if (e && e.preventDefault) e.preventDefault();
      setDeferred(e || window.__capexBip);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBip);
    window.addEventListener('capex-bip-ready', onBip);

    // iOS Safari ne déclenche pas beforeinstallprompt → invite manuelle
    let iosTimer;
    if (isIOS()) {
      iosTimer = setTimeout(() => setVisible(true), 2500);
    }

    const onInstalled = () => { setVisible(false); setDeferred(null); };
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBip);
      window.removeEventListener('capex-bip-ready', onBip);
      window.removeEventListener('appinstalled', onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    setIosHelp(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
  };

  const install = async () => {
    if (isIOS()) { setIosHelp(true); return; }
    if (!deferred) return;
    deferred.prompt();
    try {
      const { outcome } = await deferred.userChoice;
      if (outcome === 'accepted') setVisible(false);
    } catch { /* noop */ }
    setDeferred(null);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      style={{
        position: 'fixed', left: 12, right: 12, bottom: 12, zIndex: 9999,
        background: '#141416', border: '1px solid rgba(239,118,35,.45)',
        borderRadius: 16, boxShadow: '0 10px 40px rgba(0,0,0,.55)',
        padding: 14, maxWidth: 460, marginInline: 'auto',
        fontFamily: 'Sora, Inter, sans-serif', animation: 'pwaSlideUp .25s ease',
      }}
    >
      <style>{'@keyframes pwaSlideUp{from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}}'}</style>
      <button
        onClick={dismiss}
        data-testid="pwa-install-close"
        aria-label="Fermer"
        style={{ position: 'absolute', top: 8, right: 8, background: 'transparent', border: 'none', color: '#8a8a90', cursor: 'pointer', padding: 4 }}
      >
        <X style={{ width: 18, height: 18 }} />
      </button>

      {!iosHelp ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src="/capex-app-icon-192.png" alt="CAPEX" width={48} height={48} style={{ borderRadius: 12, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0, paddingRight: 18 }}>
            <div style={{ color: '#f2efe9', fontWeight: 800, fontSize: 15, lineHeight: 1.15 }}>Installer CAPEX</div>
            <div style={{ color: '#a7a7ad', fontSize: 12.5, marginTop: 2 }}>Accès rapide plein écran, comme une application.</div>
          </div>
          <button
            onClick={install}
            data-testid="pwa-install-btn"
            style={{
              flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 6,
              background: '#ef7623', color: '#141416', fontWeight: 800, fontSize: 13.5,
              border: 'none', borderRadius: 999, padding: '10px 16px', cursor: 'pointer',
            }}
          >
            <Download style={{ width: 16, height: 16 }} />Installer
          </button>
        </div>
      ) : (
        <div style={{ paddingRight: 18 }} data-testid="pwa-install-ios-help">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <img src="/capex-app-icon-192.png" alt="CAPEX" width={40} height={40} style={{ borderRadius: 10 }} />
            <div style={{ color: '#f2efe9', fontWeight: 800, fontSize: 15 }}>Ajouter CAPEX à l'écran d'accueil</div>
          </div>
          <ol style={{ color: '#c9c9cf', fontSize: 13, margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
            <li>Touchez l'icône <Share style={{ width: 15, height: 15, verticalAlign: '-3px', color: '#ef7623' }} /> <b>Partager</b> en bas de Safari.</li>
            <li>Choisissez <b>« Sur l'écran d'accueil »</b>.</li>
            <li>Validez avec <b>Ajouter</b>.</li>
          </ol>
        </div>
      )}
    </div>
  );
}
